<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogTrigger,
    } from '@/components/ui/alert-dialog';
    import { useForm } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { User } from '@/types';
    import ResetPasswordButton from '@/components/ResetPasswordButton.vue';
    import QueryKey from '@/enums/QueryKey';

    const queryClient = useQueryClient();

    const emit = defineEmits(['error']);

    const { t } = useI18n();

    interface Props {
        user: User;
    }

    const props = defineProps<Props>();

    const form = useForm({});

    const action = () => {
        form.get(route('v2.users.reset-password', props.user.id), {
            preserveScroll: true,
            onSuccess: () => {
                updateTable();
                toast.success(t(`users.js.reset.success`, { username: props.user.name }));
            },
            onError: (error) => {
                toast.error(t('forms.general.error'));
                emit('error', error);
            },
        });
    };

    const updateTable = () => {
        queryClient.invalidateQueries({ queryKey: [QueryKey.USERS] });
    };
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <ResetPasswordButton />
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t(`users.js.reset.title`) }}</AlertDialogTitle>
                <AlertDialogDescription>
                    {{ t(`users.js.reset.content_1`, { name: props.user.name }) }}
                    <br />
                    {{ t(`users.js.reset.content_2`) }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t(`users.js.no`) }}</AlertDialogCancel>
                <AlertDialogAction
                    @click="action"
                    variant="default"
                >
                    {{ t(`users.js.yes`) }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
